import type {
    LogicNode,
    TradeLogicDiagnosticsState,
    TradeLogicDocumentKind,
    TradeLogicDraft,
    TradeLogicDraftId,
    TradeLogicEvaluationMode,
    TradeLogicJsonObject,
    TradeLogicRegistryCapabilityState,
    TradeLogicScope
} from './types';

export interface CreateTradeLogicDraftInput {
    draftId: TradeLogicDraftId;
    name: string;
    kind: TradeLogicDocumentKind;
    scope: TradeLogicScope;
    schemaVersion: number;
    rootNode?: LogicNode | null;
    evaluationModes?: TradeLogicEvaluationMode[];
    registryManifestFingerprint?: string | null;
    registryCapability?: TradeLogicRegistryCapabilityState;
    diagnostics?: TradeLogicDiagnosticsState;
    description?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    metadata?: TradeLogicJsonObject;
    priceFillModel?: 'resting_level' | null;
}

export interface UpdateTradeLogicDraftMetadataInput {
    description?: string | null;
    evaluationModes?: TradeLogicEvaluationMode[];
    name?: string;
    priceFillModel?: 'resting_level' | null;
}

export const createEmptyRegistryCapabilityState = (): TradeLogicRegistryCapabilityState => ({
    loadState: 'idle',
    featuresByKey: {},
    conditionTemplatesByKey: {},
    actionTemplatesByKey: {},
    issues: []
});

export const createTradeLogicDraft = ({
    draftId,
    name,
    kind,
    scope,
    schemaVersion,
    rootNode = null,
    evaluationModes = [],
    registryManifestFingerprint = null,
    registryCapability = createEmptyRegistryCapabilityState(),
    diagnostics = { items: [] },
    description = null,
    createdAt = null,
    updatedAt = null,
    metadata,
    priceFillModel = null
}: CreateTradeLogicDraftInput): TradeLogicDraft => ({
    draftId,
    name,
    description,
    kind,
    scope,
    status: 'draft',
    schemaVersion,
    registryManifestFingerprint,
    rootNode,
    evaluationModes,
    priceFillModel,
    registryCapability,
    diagnostics,
    createdAt,
    updatedAt,
    metadata
});

export const withDraftRootNode = (draft: TradeLogicDraft, rootNode: LogicNode | null): TradeLogicDraft => ({
    ...draft,
    rootNode
});

export const withDraftDiagnostics = (draft: TradeLogicDraft, diagnostics: TradeLogicDiagnosticsState): TradeLogicDraft => ({
    ...draft,
    diagnostics
});

export const updateTradeLogicDraftMetadata = (
    draft: TradeLogicDraft,
    changes: UpdateTradeLogicDraftMetadataInput,
    updatedAt: string = new Date().toISOString()
): TradeLogicDraft => ({
    ...draft,
    description: changes.description === undefined ? draft.description : normalizeDraftDescription(changes.description),
    evaluationModes: changes.evaluationModes ?? draft.evaluationModes,
    name: changes.name === undefined ? draft.name : normalizeDraftName(changes.name, draft.name),
    priceFillModel: changes.priceFillModel === undefined ? draft.priceFillModel : changes.priceFillModel,
    updatedAt
});

const normalizeDraftDescription = (description: string | null): string | null => {
    if (description === null) return null;

    const trimmedDescription = description.trim();

    return trimmedDescription ? trimmedDescription : null;
};

const normalizeDraftName = (name: string, fallbackName: string): string => {
    const trimmedName = name.trim();

    return trimmedName ? trimmedName : fallbackName;
};
